import * as Yup from "yup";
import TranslationKeys, {
  TranslationKey,
} from "../../../../contexts/I18nContext/translationKeys";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../../../utils/yupUtils";
import { isUS } from "../../../Registration/countriesList";
import { SID } from "@securitize/domain";

export const allowedStreetCityCharsRegex = /^[a-zA-Z0-9\s.'&#/,-]+$/;
export const allowedZipCodeCharsRegex = /^[a-zA-Z0-9\s-]+$/;
export const allowedTaxIdCharsRegex = /^[a-zA-Z0-9\s-]+$/;

// allow optionally http or https prefixes, allow www. prefix, validate special chars and domain format
export const websiteFormatRegex =
  /^(?:https?:\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
export const leiCodeCharactersRegex = /^[A-Za-z0-9]+$/;

export const allowedEntityNameCharsRegex = /^[a-zA-Z0-9\s.'&#/,-]+$/;
export type AddressCardFormType = {
  street?: string;
  entrance?: string;
  city?: string;
  zip?: string;
  countryCode?: string;
  state?: string;
};

export type EntityInformationCardFormType = {
  entityName?: string;
  entityType?: SID.InvestorEntityType;
  incorporationDate?: Date;
  entityIdNumber?: string;
  businesses?: string;
};

export type BusinessInformationCardFormType = {
  website?: string;
  leiCode?: string;
  sourceOfFunds?: string;
  lineOfBusiness?: string;
};

export type EntityKYBFormType = {
  entityInformation: EntityInformationCardFormType;
  businessInformation: BusinessInformationCardFormType;
  address: AddressCardFormType;
};

export const createValidationSchema = (
  getTranslation: (
    key: TranslationKey,
    replacements?: Record<string, string | number> | undefined,
  ) => string,
  isInvestorEu: boolean,
) => {
  return Yup.object({
    entityInformation: Yup.object({
      entityName: Yup.string()
        .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
        .matches(
          allowedEntityNameCharsRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_NAME_INVALID,
          ),
        ),
      entityType: Yup.string().required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
      incorporationDate: Yup.date()
        .required(
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_FORMAT,
          ),
        )
        .max(
          new Date(),
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_INVALID,
          ),
        )
        .typeError(
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_INVALID,
          ),
        ),
      entityIdNumber: Yup.string()
        .matches(
          /^[A-Za-z0-9-\s]+$/,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_TAX_ID_INVALID,
          ),
        )
        .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
        .trim(),
    }),

    businessInformation: Yup.object({
      website: Yup.string()
        .transform((value: string) => (value === "" ? undefined : value))
        .trim()
        .matches(
          websiteFormatRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_WEBSITE_INVALID,
          ),
        ),
      leiCode: Yup.string()
        .trim()
        .test(
          "isRequiredBasedOnCountry",
          REQUIRED_GENERIC_HIDDEN_MESSAGE,
          (value) => {
            if (isInvestorEu) {
              return !!value; // Field will be valid if there is a value (required)
            }
            return true; // Field will not be required, so validation will always pass
          },
        )
        .transform((value: string) => (value === "" ? undefined : value))
        .matches(
          leiCodeCharactersRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LEI_CODE_INVALID,
          ),
        )
        .length(
          20,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LEI_CODE_LENGTH_ERROR,
          ),
        )
        .nullable(),
      sourceOfFunds: Yup.string().required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
      lineOfBusiness: Yup.string().required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
    }),
    address: Yup.object({
      // Nesting the first schema under "address"
      street: Yup.string()
        .matches(
          allowedStreetCityCharsRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STREET_INVALID,
          ),
        )
        .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
        .trim(),
      entrance: Yup.string()
        .transform((value: string) => (value === "" ? undefined : value))
        .matches(
          allowedStreetCityCharsRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_STREET_INVALID,
          ),
        )
        .trim(),
      city: Yup.string()
        .matches(
          allowedStreetCityCharsRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_CITY_INVALID,
          ),
        )
        .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
        .trim(),
      zip: Yup.string()
        .trim()
        .required(REQUIRED_GENERIC_HIDDEN_MESSAGE)
        .matches(
          allowedZipCodeCharsRegex,
          getTranslation(
            TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ZIP_INVALID,
          ),
        ),
      countryCode: Yup.string().required(),
      state: Yup.string().when("countryCode", {
        is: "US",
        then: (schema) => schema.required(REQUIRED_GENERIC_HIDDEN_MESSAGE),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });
};

// We won't allow US country with no state
export const areCountryCodeAndStateValid = (
  countryCode: string | undefined,
  state: string | undefined,
) => {
  return !(isUS(countryCode || "") && !state);
};
